import { IconCloud, IconCpu, IconSend, IconX } from '@tabler/icons-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@/components/common';
import { browserName } from '@/utils/browser';
import type { OnDevice } from '../hooks/useSpeechRecognition';
import type { Clarification, ClarificationOption } from '../parser';
import { QuestionCard } from './QuestionCard';

type Props = {
  listening: boolean;
  typing: boolean;
  supported: boolean;
  interim: string;
  lines: string[];
  questions: Clarification[];
  error: string | null;
  onDevice: OnDevice;
  onInstallOnDevice: () => void;
  onSubmitText: (text: string) => void;
  onResolve: (id: string, option: ClarificationOption) => void;
  onDismiss: (id: string) => void;
  onManual: (q: Clarification) => void;
  onClose: () => void;
};

function PanelHeader({ p }: { p: Props }) {
  const { t } = useTranslation();
  const title = p.listening
    ? t('dictation.listening')
    : p.typing
      ? t('dictation.typed')
      : t('dictation.question');
  return (
    <div className="row">
      <strong className="grow">{title}</strong>
      {p.listening && <RecognitionMode onDevice={p.onDevice} />}
      {!p.listening && (
        <button
          type="button"
          className="icon-btn"
          onClick={p.onClose}
          aria-label={t('common.close')}
        >
          <IconX size={20} />
        </button>
      )}
    </div>
  );
}

/** Tells the clinician whether audio stays on the device. */
function RecognitionMode({ onDevice }: { onDevice: OnDevice }) {
  const { t } = useTranslation();
  return onDevice === 'yes' ? (
    <span className="badge badge--approved" title={t('dictation.onDeviceTitle')}>
      <IconCpu size={12} /> {t('dictation.onDevice')}
    </span>
  ) : (
    <span className="badge badge--pending" title={t('dictation.cloudTitle')}>
      <IconCloud size={12} /> {t('dictation.cloud')}
    </span>
  );
}

const VISIBLE_LINES = 3;

function Transcript({
  lines,
  interim,
  listening,
}: {
  lines: string[];
  interim: string;
  listening: boolean;
}) {
  const { t } = useTranslation();
  if (!(lines.length || interim)) {
    return listening ? (
      <p className="muted small">
        {t('dictation.examples', {
          first: t('dictation.example1'),
          second: t('dictation.example2'),
        })}
      </p>
    ) : null;
  }
  // The full text is in the chart's transcript card; here only the latest lines.
  const latest = lines.slice(-VISIBLE_LINES);
  return (
    <div className="transcript">
      {latest.map((l, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the latest lines of the dictation
        <div key={i} className="transcript__final">
          {l}
        </div>
      ))}
      {interim && <div className="transcript__interim">{interim}</div>}
    </div>
  );
}

function TypedInput({ onSubmit }: { onSubmit: (text: string) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(text);
    setText('');
  };
  return (
    <form className="row" onSubmit={submit} style={{ flexWrap: 'nowrap' }}>
      <input
        className="input"
        placeholder={t('dictation.placeholder', { example: t('dictation.example3') })}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={t('dictation.typeLabel')}
        // biome-ignore lint/a11y/noAutofocus: the panel opens on purpose to type
        autoFocus
      />
      <button
        type="submit"
        className="icon-btn"
        aria-label={t('dictation.send')}
        disabled={!text.trim()}
      >
        <IconSend size={22} />
      </button>
    </form>
  );
}

const VISIBLE_QUESTIONS = 2;

/** The newest questions first; older ones folded behind "+N more" to keep the panel short. */
function QuestionList({
  questions,
  onResolve,
  onDismiss,
  onManual,
}: Pick<Props, 'questions' | 'onResolve' | 'onDismiss' | 'onManual'>) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const newestFirst = [...questions].reverse();
  const shown = all ? newestFirst : newestFirst.slice(0, VISIBLE_QUESTIONS);
  const hidden = newestFirst.length - shown.length;
  return (
    <>
      {shown.map((q) => (
        <QuestionCard
          key={q.id}
          question={q}
          onResolve={(o) => onResolve(q.id, o)}
          onDismiss={() => onDismiss(q.id)}
          onManual={() => onManual(q)}
        />
      ))}
      {(hidden > 0 || all) && newestFirst.length > VISIBLE_QUESTIONS && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          aria-expanded={all}
          onClick={() => setAll(!all)}
        >
          {all ? t('dictation.fewerQuestions') : t('dictation.moreQuestions', { count: hidden })}
        </button>
      )}
    </>
  );
}

export function DictationPanel(p: Props) {
  const { t } = useTranslation();
  const visible = p.listening || p.typing || p.questions.length > 0 || !!p.error;
  if (!visible) return null;
  const showInput = (p.typing || !p.supported) && !p.listening;

  return (
    <div className="dictation">
      <div className="dictation__inner" aria-live="polite">
        <PanelHeader p={p} />
        {p.error && <Banner tone="error">{p.error}</Banner>}
        {!p.supported && (
          <Banner tone="info">
            {t('dictation.noRecognition', { browser: browserName() ?? t('dictation.thisBrowser') })}
          </Banner>
        )}
        {p.listening && p.onDevice === 'downloadable' && (
          <Banner
            tone="warning"
            action={
              <button type="button" className="btn btn--sm" onClick={p.onInstallOnDevice}>
                {t('dictation.download')}
              </button>
            }
          >
            {t('dictation.downloadHint')}
          </Banner>
        )}
        <Transcript lines={p.lines} interim={p.interim} listening={p.listening} />
        <QuestionList
          questions={p.questions}
          onResolve={p.onResolve}
          onDismiss={p.onDismiss}
          onManual={p.onManual}
        />
        {showInput && <TypedInput onSubmit={p.onSubmitText} />}
      </div>
    </div>
  );
}
